import type React from 'react';

import { skipToken } from '@reduxjs/toolkit/query';

import { Urls, type ColumnNames } from '../types/ExtendEnums';
import type { Ventetid, VentetiderForBehandlingBehandlingssted } from '../types/Helsenorge.VelgBehandlingssted.Libraries.EntitiesEntities';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import Button from '@helsenorge/designsystem-react/components/Button';
import LinkList from '@helsenorge/designsystem-react/components/LinkList';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Title from '@helsenorge/designsystem-react/components/Title';

import { getHelsenorgeUrl } from '@helsenorge/framework-utils/hn-proxy-service';

import VentetidComponent from './ventetid';
import { sortVentetider } from '../behandlingssteder/table-helper';
import AdresseComponent from '../shared/adresse';
import TelefonComponent from '../shared/telefon';
import { useGetBehandlingLenkeQuery } from '../store/api/velgBehandlingsstedApi';
import { hentHelseregionAvtaleString } from '../utils/strings';

export interface TabellData {
  behandlingsstedData: VentetiderForBehandlingBehandlingssted;
  [ColumnNames.Behandlingssted]: string;
  ventetid: string | undefined;
}

interface Props {
  rowData?: TabellData;
  expanded?: boolean;
  resources: HNVelgBehandlingsstedFrontend;
  bid: string;
}

const BehandlingsstedExpanded: React.FunctionComponent<Props> = ({ rowData, expanded, resources, bid }: Props) => {
  const behandlingsstedData = rowData?.behandlingsstedData;
  const orgNr = behandlingsstedData?.orgnummer;
  const showContent = !!expanded;
  const harVentetider = !!behandlingsstedData?.ventetider && behandlingsstedData.ventetider.length > 0;
  const seVentetiderUrl = `${getHelsenorgeUrl()}${Urls.SlikVelgerDu}`;

  const getHostname = (url: string): string => {
    const domain = new URL(url);
    return domain.hostname.replace('www.', '');
  };

  const ventetider = behandlingsstedData?.ventetider ? [...behandlingsstedData.ventetider] : undefined;

  ventetider?.sort(sortVentetider);

  const { data: lenker } = useGetBehandlingLenkeQuery(orgNr && expanded ? { behandlingId: bid, orgNr } : skipToken);

  const behandlingLenker = lenker?.behandlingLenker?.flatMap(lenke => {
    if (lenke.behandlingTittel && lenke.behandlingLenke) {
      return [
        {
          text: lenke.behandlingTittel,
          url: lenke.behandlingLenke,
        },
      ];
    }

    return [];
  });

  const avdelingLenker = lenker?.behandlingLenker?.flatMap(lenke => {
    if (lenke.avdelingTittel && lenke.avdelingLenke) {
      return [
        {
          text: lenke.avdelingTittel,
          url: lenke.avdelingLenke,
        },
      ];
    }

    return [];
  });

  return showContent && behandlingsstedData ? (
    <div className="vbs-main__expanded-inner-content">
      <Title htmlMarkup="h2" appearance={'title2'} margin={1}>
        {behandlingsstedData.navn}
      </Title>

      {behandlingsstedData.helseRegioner || behandlingsstedData.avtaleMedHelfo || !!behandlingsstedData.kommentar ? (
        <section>
          {behandlingsstedData.avtaleMedHelfo && <span>{resources.ventetiderForBehandlingAvtaleMedHelfo}</span>}
          <br />
          {behandlingsstedData.helseRegioner && behandlingsstedData.erPrivat ? (
            <div>
              {behandlingsstedData.helseRegioner.map((region: string) => (
                <div key={region}>{hentHelseregionAvtaleString(region, resources)}</div>
              ))}
            </div>
          ) : null}

          {!!behandlingsstedData.kommentar && <p>{behandlingsstedData.kommentar}</p>}
        </section>
      ) : null}

      {harVentetider && (
        <Title htmlMarkup="h3" appearance={'title3'} margin={0.5}>
          {resources.ventetiderForBehandlingTabellVentetid}
        </Title>
      )}

      {harVentetider || !behandlingsstedData.harVurderingskompetanse ? (
        <p>
          {ventetider?.map((ventetid: Ventetid, index: number) => (
            <VentetidComponent key={ventetid.type} resources={resources} ventetid={ventetid} lineBreak={index < ventetider.length - 1} />
          ))}

          {!behandlingsstedData.harVurderingskompetanse && (
            <span>
              <br />
              <strong>{resources.ventetiderForBehandlingHenvisningVurderingObs} </strong>
              {resources.ventetiderForBehandlingHenvisningVurdering}
            </span>
          )}
        </p>
      ) : null}

      {behandlingLenker?.length && (
        <>
          <Title htmlMarkup="h3" appearance={'title3'} margin={1}>
            {resources.behandlingsstederTabellBehandling}
          </Title>
          <LinkList>
            {behandlingLenker?.map(lenke => (
              <LinkList.Link key={lenke.url} target="_blank" href={lenke.url} external>
                {`${lenke.text} (${getHostname(lenke.url)})`}
              </LinkList.Link>
            ))}
          </LinkList>
          <Spacer />
        </>
      )}

      {avdelingLenker?.length && (
        <>
          <Title htmlMarkup="h3" appearance={'title3'} margin={1}>
            {resources.ventetiderForBehandlingKontakt}
          </Title>
          <LinkList>
            {avdelingLenker?.map(lenke => (
              <LinkList.Link key={lenke.url} target="_blank" href={lenke.url} external>
                {`${lenke.text} (${getHostname(lenke.url)})`}
              </LinkList.Link>
            ))}
          </LinkList>
          <Spacer />
        </>
      )}

      <div className="vbs-ventetider-for-behandling__contact-info">
        <div>
          {!!behandlingsstedData.besoksAdresse && !!behandlingsstedData.besoksAdresse.gateadresse && (
            <>
              <Title htmlMarkup="h4" appearance={'title4'} margin={0}>
                {resources.tabellBesoksadresse}
              </Title>
              <AdresseComponent adresse={behandlingsstedData.besoksAdresse} />
            </>
          )}

          {behandlingsstedData.telefon && (
            <>
              <Title htmlMarkup="h4" appearance={'title4'} margin={0}>
                {resources.tabellTelefon}
              </Title>
              <TelefonComponent telefon={behandlingsstedData.telefon} />
            </>
          )}
        </div>

        <div>
          {!!behandlingsstedData.postAdresse && !!behandlingsstedData.postAdresse.gateadresse && (
            <>
              <Title htmlMarkup="h4" appearance={'title4'} margin={0}>
                {resources.ventetiderForBehandlingPostadresse}
              </Title>
              <AdresseComponent adresse={behandlingsstedData.postAdresse} />
            </>
          )}
        </div>
      </div>

      <Button href={seVentetiderUrl} htmlMarkup="a" wrapperClassName="vbs-ventetider-for-behandling__link">
        {resources.ventetiderForBehandlingVelgBehandlingssted}
      </Button>
    </div>
  ) : null;
};

export default BehandlingsstedExpanded;
